import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import keyBy from 'lodash.keyby';
import flatten from 'lodash.flatten';
import { deepCamelCase } from '@amplify/web-assessment-common/dist/utils';
import {
    AVAILABLE_SKILL_NAMES_BY_MEASURE_NAMES,
    MEASURE_NAMES,
    SKILL_NAMES,
} from '../../constants/assessments';
import { DEFAULT_APP_CONFIG } from '../../constants/config';
import { IDEL2, IDEL2_EDITIONS } from '../../constants/applications';
import { useFeatureToggleContext } from './FeatureToggleContext';
import { DEFAULT_FLO_PM_POPUP_LINK } from '../navigation/navigation';

export const AppConfigContext = React.createContext();
AppConfigContext.displayName = 'AppConfigContext';

export const useAppConfigContext = () => React.useContext(AppConfigContext);

/**
 * Extends array of measureItems with required for dashboard displaying keys.
 * @param {Object} measureItems array of measure names along with applicable grades
 * @returns {Array} with objects, each of them contains applicable grades, isInventory, measure and skillName.
 */
export const getLicenses = measureItems =>
    flatten(
        measureItems.map(measureItem =>
            get(AVAILABLE_SKILL_NAMES_BY_MEASURE_NAMES, measureItem.measure, []).map(skillName =>
                deepCamelCase({
                    ...measureItem,
                    skillName,
                }),
            ),
        ),
    );

/**
 * Compares two config objects. Verifies that objects contains
 * equal attribute values.
 * @param {Object} defaultConfig measure config object
 * @param {Object} licensedConfig measure config object
 * @returns {boolean} results of comparison
 */
const areConfigsEqual = (defaultConfig, licensedConfig) => {
    // Why grades attribute should be omitted?
    // If default config attributes is equal to licensed config attributes,
    // grades in default config will be replaced by grades from licensed config.
    // Otherwise licensed config will be added to the end of merged config.
    const omittedAttrs = ['grades'];
    const defAttrs = Object.keys(defaultConfig).filter(attr => !omittedAttrs.includes(attr));
    const licAttrs = Object.keys(licensedConfig).filter(attr => !omittedAttrs.includes(attr));
    if (defAttrs.length !== licAttrs.length) {
        return false;
    }
    return licAttrs.every(licAttr => licensedConfig[licAttr] === get(defaultConfig, licAttr));
};

/**
 * Resolve conflicts between default application configs and configs that user is licensed to.
 * If only one is provided, then set config will be returned.
 * If none is provided, empty list is returned.
 * If two configs are provided, licensing config overrides default configuration on measure level,
 * i.e. all default config items are included, possibly extended with licensed items, and if
 * the same measure is in both lists, licensed config wins.
 * @param {Array<Object>} [defaultConfig=[]] list of default configs, should contain measure key
 * @param {Array<Object>} [licensedConfig=[]] list of licensed configs, should contain measure key
 * @returns {Array<Object>} list of configs that take into account default and licensing
 */
export const mergeConfigs = (defaultConfig = [], licensedConfig = []) => {
    if (licensedConfig.length === 0) {
        return defaultConfig;
    }
    if (defaultConfig.length === 0) {
        return licensedConfig;
    }

    const defaultConfigMap = keyBy(defaultConfig, 'measure');
    const { configsToOverride, configsToAdd } = licensedConfig.reduce(
        (acc, value) => {
            if (areConfigsEqual(get(defaultConfigMap, value.measure, {}), value)) {
                acc.configsToOverride = [...acc.configsToOverride, value];
            } else {
                acc.configsToAdd = [...acc.configsToAdd, value];
            }
            return acc;
        },
        { configsToOverride: [], configsToAdd: [] },
    );

    const configsToOverrideMap = keyBy(configsToOverride, 'measure');

    return [...Object.values({ ...defaultConfigMap, ...configsToOverrideMap }), ...configsToAdd];
};

// TODO: Remove this function when Websync will provide
// optional FSL K-Inisio and QQ measures as optional
/**
 * HACK: Function will replace regular FSLIK measure with optional FSLIK
 * @param licensedMeasures
 * @returns {[{skillName: string, measure: string, grades: string[], isOptional: boolean, isInventory: boolean},...*]|*}
 */
const replaceMeasures = licensedMeasures => {
    const measures = licensedMeasures.map(({ measure }) => measure);
    let filteredLicensedMeasures = [...licensedMeasures];

    // TODO: remove after 2023 rollover
    if (measures.includes(MEASURE_NAMES.FSLIK)) {
        filteredLicensedMeasures = filteredLicensedMeasures.filter(
            ({ measure }) => measure !== MEASURE_NAMES.FSLIK,
        );
        filteredLicensedMeasures.push({
            measure: MEASURE_NAMES.FSLIK,
            skillName: SKILL_NAMES.SONIDOS_DE_LETRAS,
            grades: ['KG'],
            isOptional: true,
            isInventory: false,
        });
    }
    if (measures.includes(MEASURE_NAMES.QQ)) {
        filteredLicensedMeasures = filteredLicensedMeasures.filter(
            ({ measure }) => measure !== MEASURE_NAMES.QQ,
        );
        filteredLicensedMeasures.push({
            measure: MEASURE_NAMES.QQ,
            skillName: SKILL_NAMES.CONCIENCIA_FONOLOGICA,
            grades: ['KG', '01', '02'],
            isOptional: true,
            isInventory: false,
        });
    }

    if (measures.includes(MEASURE_NAMES.FNL2)) {
        filteredLicensedMeasures = filteredLicensedMeasures.filter(
            ({ measure }) => measure !== MEASURE_NAMES.FNL2 && measure !== MEASURE_NAMES.FNL,
        );

        filteredLicensedMeasures.push({
            measure: MEASURE_NAMES.FNL,
            skillName: SKILL_NAMES.NOMBRAR_LETRAS,
            grades: ['KG', '01', '02'],
            isOptional: false,
            isInventory: false,
        });
    }

    return filteredLicensedMeasures;
};

// TODO: Remove this function when Websync will provide
// optional FSL K-Inisio and QQ measures as optional
/**
 * HACK: Deletes redundant FLS K-Inisio and QQ measures
 * received from Websync
 * @param {Object} appConfig application config generated
 *      by mergeConfigs function
 * @returns {Object} actual config
 */
const adjustApplicationConfig = appConfigs => {
    if (Object.keys(appConfigs).includes(IDEL2)) {
        const { licensedMeasures } = appConfigs[IDEL2];

        return {
            ...appConfigs,
            [IDEL2]: {
                ...appConfigs[IDEL2],
                licensedMeasures: replaceMeasures(licensedMeasures),
            },
        };
    }

    return appConfigs;
};

/**
 * Function removes FSLIK measure from default app config according
 * to feature toggle. Function should be removed after 2023 rollover.
 * @param {Object} defaultConfig object with default application configuration
 * @param {Boolean} isFSLIKDisabledInGradeK - FSLIK measure feature toggle
 * @returns {Object} object with default configs
 */
const getDefaultConfig = (defaultConfig, isFSLIKDisabledInGradeK) => {
    if (isFSLIKDisabledInGradeK) {
        return {
            ...defaultConfig,
            [IDEL2]: {
                ...defaultConfig[IDEL2],
                config: {
                    measures: [],
                },
            },
        };
    }
    return defaultConfig;
};

/**
 * Translates application configs.
 * If measure config is not provided by licensed application, default config is taken.
 * @param {Object} applications application map
 * @param {String} dashboard edition config string
 * @param {Boolean} isFSLIKDisabledInGradeK feature toggle that enable or disable FSLIK measure in
 *                                        grade K. Should be deleted after 2023 rollover
 * @returns {Object} translated application configs by application
 */
export const getAppConfigs = (applications, isFSLIKDisabledInGradeK) => {
    return adjustApplicationConfig(
        Object.keys(applications).reduce(
            (configs, appName) => ({
                ...configs,
                [appName]: {
                    licensedMeasures: getLicenses(
                        mergeConfigs(
                            // TODO: return regular default config after rollover
                            get(getDefaultConfig(DEFAULT_APP_CONFIG, isFSLIKDisabledInGradeK), [
                                appName,
                                'config',
                                'measures',
                            ]),
                            get(applications, [appName, 'config', 'measures']),
                        ),
                    ),
                },
            }),
            {},
        ),
    );
};

const filterAppConfigsForSecureAssessments = (
    isSecureAssessmentsEnabled,
    hideDidels8WithIntervention,
    appConfigs,
) => {
    if (isSecureAssessmentsEnabled && hideDidels8WithIntervention) {
        // skip Dibels8, Lectura and OLS assessments so far
        // as tey should be passed in OWAU
        // eslint-disable-next-line no-unused-vars
        const { dibels8, idel2, ols, olsspanish, ...rest } = appConfigs;
        return {
            ...rest,
        };
    }
    return appConfigs;
};

const AppConfigContextProvider = ({
    children,
    applications,
    edition,
    floPMPopupLink,
    isSecureAssessmentsEnabled,
}) => {
    // TODO: Remove this feature toggle after 2023 rollover
    const { isFSLIKDisabledInGradeK, hideDidels8WithIntervention } = useFeatureToggleContext();
    const appConfigs = getAppConfigs(applications, isFSLIKDisabledInGradeK);
    const filteredAppConfig = filterAppConfigsForSecureAssessments(
        isSecureAssessmentsEnabled,
        hideDidels8WithIntervention,
        appConfigs,
    );
    const isLecturaLicensed = applications?.[IDEL2]?.editionName === IDEL2_EDITIONS.LECTURA;

    return (
        <AppConfigContext.Provider
            value={{
                appConfigs: filteredAppConfig,
                appPmConfigs: appConfigs,
                edition,
                floPMPopupLink,
                isLecturaLicensed,
                isSecureAssessmentsEnabled,
            }}
        >
            {children}
        </AppConfigContext.Provider>
    );
};

AppConfigContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
    applications: PropTypes.object.isRequired,
    edition: PropTypes.string,
    floPMPopupLink: PropTypes.string,
    isSecureAssessmentsEnabled: PropTypes.bool,
};

AppConfigContextProvider.defaultProps = {
    edition: '',
    floPMPopupLink: DEFAULT_FLO_PM_POPUP_LINK,
    isSecureAssessmentsEnabled: false,
};

export default AppConfigContextProvider;
